import { brandsData } from './genericData';
import type { StyleRow, CustomFieldRowEntry } from './stylesFixtureData';

/**
 * Deterministic probes for the styles bulk-edit field-availability/conditional-gating epic
 * (DL-62593, Testmo folder "Bulk edit styles"). All isolable together via `?search=BulkEditProbe`
 * (unique substring, absent from every generated/other fixture name) and all in editable style
 * states so the bulk-edit warning modal never intercepts selection.
 *
 * - `bulkEditProbeStateUnpublishedA/B` + `bulkEditProbeStatePublished` — Supplier field bulk-edit
 *   gating (Testmo 1577184): `styleFieldAccess.styleSupplier.bulkEditDisabledWhen` is
 *   `anySelectedHasPublishedState`, i.e. disabled unless EVERY selected style is `unpublished`.
 * - `bulkEditProbeSupplierEmptyA/B`, `bulkEditProbeSupplierSameA/B`, `bulkEditProbeSupplierDiff` —
 *   Supplier Contact bulk-edit gating (Testmo 1577185 / 1577269), mirroring the item probes in
 *   `mocks/api/handlers/items.ts`.
 * - `bulkEditProbeMotherSameA/B` / `bulkEditProbeMotherDiff` — Hierarchy Mother/Child bulk-edit
 *   gating (Testmo 1577186 / 1577266 / 1577265), reusing the Print Type (CF705, mother) / Print
 *   Placement (CF706, child) NestedAllowedValue pair already seeded in
 *   `styleCustomFieldDefinitions` in `styles.ts` (same pair the inline-edit suite exercises).
 * - `bulkEditProbeCategoriesA/B` — multi-value (Categories) bulk-edit REPLACE semantics
 *   (Testmo 1577188): two disjoint category sets so a picked new value can only be observed as a
 *   replace, never a merge.
 * - `bulkEditProbeScopedAppliesA` / `bulkEditProbeScopedNotAppliesB/C` — scoped custom field
 *   (Sustainability Rating, CF707) exclusion from the bulk-edit modal (Testmo 1577187), covering
 *   both the "some selected apply" (disabled-with-tooltip) and "none apply" (hidden) branches of
 *   `classifyCf`/`buildScopedBulkCfColumns` (`features/styles/utils/customFieldScope.ts`).
 * - `bulkEditProbeMixedSeasonA/B` — "Mixed values" preview (Testmo 1577177): identical in every
 *   dimension except Season, so the Season row is the only one that reads "Mixed values".
 * - `bulkEditProbeBrandContactA/B` — "Mixed values" preview for the literal field the Testmo
 *   case names (1577177's own steps: "select 2 styles with different Brand Contact values").
 *   Same `brand.id` (so the "Brand" row itself is never mixed) but different
 *   `brand.contactPersonId`/`contactPersonName` — the wire fields `getStyles.ts` synthesizes into
 *   the top-level `companyContactPerson` object that `bulkEditCols.companyContactPerson`
 *   ("Brand contact") reads for its Preview column.
 *
 * Split out of `styles.ts` — same reason `itemsFixtureData.ts`/`bulkUpdateProbeFixtureData.ts`
 * were split out of `items.ts`: keeps the handler file under the repo's 800-line cap
 * (`frontend/CLAUDE.md`'s "Max 800 lines per file"). `styles.ts`'s `seasons`/`groups`/
 * `suppliers`/`contactPersons` arrays can't be imported here without a circular dependency
 * (`styles.ts` imports the probes from this file), so the handful of base values these probes
 * need are duplicated below as their own literals — verified to match `styles.ts`'s
 * `seasons[0]`/`seasons[1]`, `groups[0]`, `suppliers[0]`/`suppliers[1]` and
 * `contactPersons[0..2]` exactly. `StyleRow`/`CustomFieldRowEntry` are imported as types only
 * (`import type`), which is erased at compile time and does not create a runtime cycle.
 */

const PROBE_SEASON_0 = { id: 26, customId: 'AW25', name: 'AW25', project: '' };
const PROBE_SEASON_1 = { id: 28, customId: 'AW26', name: 'AW26', project: '' };
const PROBE_GROUP_0 = { id: 71, customId: 'MEN', name: 'Menswear' };
const PROBE_SUPPLIER_0 = { id: 44, customId: 'S-44', name: 'Ningbo garment supplier' };
const PROBE_SUPPLIER_1 = { id: 45, customId: 'S-45', name: 'Shanghai apparel co.' };
const PROBE_CONTACT_0 = { id: 'd47e406b-d5db-4787-ade6-7225780b5b5c', name: 'Sara Johnson' };
const PROBE_CONTACT_1 = { id: 'a12b3c4d-e5f6-7890-abcd-ef1234567890', name: 'Mike Chen' };
const PROBE_CONTACT_2 = { id: 'b23c4d5e-f6a7-8901-bcde-f12345678901', name: 'Anna Larsen' };

/** Mirrors `styles.ts`'s `brandOptions` derivation exactly (same seeded `brandsData`). */
const probeBrandOptions =
  brandsData.length > 0
    ? brandsData
    : [
        { id: 29, name: 'Brand A', userDefinedId: '1' },
        { id: 30, name: 'Brand B', userDefinedId: '2' },
        { id: 31, name: 'Brand C', userDefinedId: '3' },
      ];

const bulkEditProbeStateUnpublishedA: StyleRow = {
  id: 90340,
  name: 'BulkEditProbe State Unpublished A',
  customId: 'ST-90340',
  description: null,
  state: 'unpublished',
  logo: '',
  season: PROBE_SEASON_0,
  group: PROBE_GROUP_0,
  brand: {
    id: probeBrandOptions[0].id,
    customId: probeBrandOptions[0].userDefinedId ?? '',
    name: probeBrandOptions[0].name,
    contactPersonId: PROBE_CONTACT_0.id,
    contactPersonName: PROBE_CONTACT_0.name,
  },
  supplier: null,
  categories: [],
  styleColors: [],
  customFields: [],
  properties: [],
  lastUpdated: '2026-01-01T00:00:00',
  readyForExport: false,
};

const bulkEditProbeStateUnpublishedB: StyleRow = {
  ...bulkEditProbeStateUnpublishedA,
  id: 90341,
  name: 'BulkEditProbe State Unpublished B',
  customId: 'ST-90341',
};

const bulkEditProbeStatePublished: StyleRow = {
  ...bulkEditProbeStateUnpublishedA,
  id: 90342,
  name: 'BulkEditProbe State Published',
  customId: 'ST-90342',
  state: 'published',
};

const bulkEditProbeSupplierEmptyA: StyleRow = {
  ...bulkEditProbeStateUnpublishedA,
  id: 90343,
  name: 'BulkEditProbe Supplier Empty A',
  customId: 'ST-90343',
};

const bulkEditProbeSupplierEmptyB: StyleRow = {
  ...bulkEditProbeSupplierEmptyA,
  id: 90344,
  name: 'BulkEditProbe Supplier Empty B',
  customId: 'ST-90344',
};

const bulkEditProbeSupplierSameA: StyleRow = {
  ...bulkEditProbeStateUnpublishedA,
  id: 90345,
  name: 'BulkEditProbe Supplier Same A',
  customId: 'ST-90345',
  supplier: {
    id: PROBE_SUPPLIER_0.id,
    customId: PROBE_SUPPLIER_0.customId,
    name: PROBE_SUPPLIER_0.name,
    contactPersonId: PROBE_CONTACT_0.id,
    contactPersonName: PROBE_CONTACT_0.name,
  },
};

const bulkEditProbeSupplierSameB: StyleRow = {
  ...bulkEditProbeSupplierSameA,
  id: 90346,
  name: 'BulkEditProbe Supplier Same B',
  customId: 'ST-90346',
};

const bulkEditProbeSupplierDiff: StyleRow = {
  ...bulkEditProbeSupplierSameA,
  id: 90347,
  name: 'BulkEditProbe Supplier Diff',
  customId: 'ST-90347',
  // Different supplier from the "Same" pair — drives the mixed-suppliers disabled case.
  supplier: {
    id: PROBE_SUPPLIER_1.id,
    customId: PROBE_SUPPLIER_1.customId,
    name: PROBE_SUPPLIER_1.name,
    contactPersonId: PROBE_CONTACT_2.id,
    contactPersonName: PROBE_CONTACT_2.name,
  },
};

/** Print Type (CF705, mother) entry — NestedAllowedValue, no parentCustomFieldId. */
const printTypeCf = (allowedValueId: number, label: string): CustomFieldRowEntry => ({
  id: 705,
  type: 'nestedallowedvalue',
  value: { id: allowedValueId, value: label },
});

/** Print Placement (CF706, child of CF705) entry — NestedAllowedValue. */
const printPlacementCf = (allowedValueId: number, label: string): CustomFieldRowEntry => ({
  id: 706,
  type: 'nestedallowedvalue',
  value: { id: allowedValueId, value: label },
});

const bulkEditProbeMotherSameA: StyleRow = {
  ...bulkEditProbeStateUnpublishedA,
  id: 90348,
  name: 'BulkEditProbe Mother Same A',
  customId: 'ST-90348',
  customFields: [printTypeCf(7051, 'Screen Print'), printPlacementCf(7061, 'Chest')],
};

const bulkEditProbeMotherSameB: StyleRow = {
  ...bulkEditProbeMotherSameA,
  id: 90349,
  name: 'BulkEditProbe Mother Same B',
  customId: 'ST-90349',
  customFields: [printTypeCf(7051, 'Screen Print'), printPlacementCf(7061, 'Chest')],
};

const bulkEditProbeMotherDiff: StyleRow = {
  ...bulkEditProbeMotherSameA,
  id: 90350,
  name: 'BulkEditProbe Mother Diff',
  customId: 'ST-90350',
  // Different mother allowed-value from the "Same" pair, and no child value at all.
  customFields: [printTypeCf(7052, 'Embroidery')],
};

const bulkEditProbeCategoriesA: StyleRow = {
  ...bulkEditProbeStateUnpublishedA,
  id: 90351,
  name: 'BulkEditProbe Categories A',
  customId: 'ST-90351',
  categories: [{ id: 87, customId: 'TOP', name: 'Tops' }],
};

const bulkEditProbeCategoriesB: StyleRow = {
  ...bulkEditProbeStateUnpublishedA,
  id: 90352,
  name: 'BulkEditProbe Categories B',
  customId: 'ST-90352',
  categories: [
    { id: 88, customId: 'BOT', name: 'Bottoms' },
    { id: 89, customId: 'OUT', name: 'Outerwear' },
  ],
};

const bulkEditProbeScopedAppliesA: StyleRow = {
  ...bulkEditProbeStateUnpublishedA,
  id: 90353,
  name: 'BulkEditProbe Scoped Applies A',
  customId: 'ST-90353',
  group: { id: 71, customId: 'MEN', name: 'Menswear' },
  categories: [{ id: 87, customId: 'TOP', name: 'Tops' }],
  customFields: [
    { id: 707, type: 'allowedvalue', value: { id: 7073, value: 'Gold' }, appliesToStyle: true },
  ],
};

const bulkEditProbeScopedNotAppliesB: StyleRow = {
  ...bulkEditProbeStateUnpublishedA,
  id: 90354,
  name: 'BulkEditProbe Scoped NotApplies B',
  customId: 'ST-90354',
  group: { id: 72, customId: 'WMN', name: 'Womenswear' }, // group mismatch only
  categories: [{ id: 87, customId: 'TOP', name: 'Tops' }],
  customFields: [{ id: 707, type: 'allowedvalue', value: null, appliesToStyle: false }],
};

const bulkEditProbeScopedNotAppliesC: StyleRow = {
  ...bulkEditProbeStateUnpublishedA,
  id: 90355,
  name: 'BulkEditProbe Scoped NotApplies C',
  customId: 'ST-90355',
  group: { id: 71, customId: 'MEN', name: 'Menswear' },
  categories: [{ id: 88, customId: 'BOT', name: 'Bottoms' }], // category mismatch only
  customFields: [{ id: 707, type: 'allowedvalue', value: null, appliesToStyle: false }],
};

const bulkEditProbeMixedSeasonA: StyleRow = {
  ...bulkEditProbeStateUnpublishedA,
  id: 90356,
  name: 'BulkEditProbe Mixed Season A',
  customId: 'ST-90356',
  season: PROBE_SEASON_0,
};

const bulkEditProbeMixedSeasonB: StyleRow = {
  ...bulkEditProbeStateUnpublishedA,
  id: 90357,
  name: 'BulkEditProbe Mixed Season B',
  customId: 'ST-90357',
  season: PROBE_SEASON_1,
};

const bulkEditProbeBrandContactA: StyleRow = {
  ...bulkEditProbeStateUnpublishedA,
  id: 90358,
  name: 'BulkEditProbe Brand Contact A',
  customId: 'ST-90358',
  // Same brand id/name as the base fixture (PROBE_CONTACT_0) — only the contact differs below.
};

const bulkEditProbeBrandContactB: StyleRow = {
  ...bulkEditProbeStateUnpublishedA,
  id: 90359,
  name: 'BulkEditProbe Brand Contact B',
  customId: 'ST-90359',
  brand: {
    // Same brand id/name as bulkEditProbeStateUnpublishedA's own `brand` above — different
    // contact only, so "Brand" stays shared while "Brand contact" ("companyContactPerson") is
    // the one Preview row that mixes.
    id: probeBrandOptions[0].id,
    customId: probeBrandOptions[0].userDefinedId ?? '',
    name: probeBrandOptions[0].name,
    contactPersonId: PROBE_CONTACT_1.id,
    contactPersonName: PROBE_CONTACT_1.name,
  },
};

/** Pushed as a block into `stylesData` by `styles.ts` on a fresh (non-restored) seed. */
export const stylesBulkEditProbes: StyleRow[] = [
  bulkEditProbeStateUnpublishedA,
  bulkEditProbeStateUnpublishedB,
  bulkEditProbeStatePublished,
  bulkEditProbeSupplierEmptyA,
  bulkEditProbeSupplierEmptyB,
  bulkEditProbeSupplierSameA,
  bulkEditProbeSupplierSameB,
  bulkEditProbeSupplierDiff,
  bulkEditProbeMotherSameA,
  bulkEditProbeMotherSameB,
  bulkEditProbeMotherDiff,
  bulkEditProbeCategoriesA,
  bulkEditProbeCategoriesB,
  bulkEditProbeScopedAppliesA,
  bulkEditProbeScopedNotAppliesB,
  bulkEditProbeScopedNotAppliesC,
  bulkEditProbeMixedSeasonA,
  bulkEditProbeMixedSeasonB,
  bulkEditProbeBrandContactA,
  bulkEditProbeBrandContactB,
];
